import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { CreateWorkOrderRequest, Work, WorkOrderTypes } from "../../../types/workOrderTypes";
import { createWorkOrder, getWorkOrdersForObject } from "../../../utils/FDVUtils";
import ArrowDownIcon from "../../../assets/arrow-down.svg?react";
import OpenInNewWindowIcon from "../../../assets/openInNew.svg?react";
import { AppConfig } from "../../../AppConfig";
import "./FeatureWorkOrderGroup.css";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import NfsDropdown from "../../genericComponents/NfsDropdown/NfsDropdown";
import Loader from "../../genericComponents/Loader/Loader";
import { toast } from "react-toastify";
import InputField from "../../genericComponents/InputField/InputField";
import { NFSStatus } from "../../../utils/nfsUtils";
import Checkbox from "../../genericComponents/Checkbox/Checkbox";
import type Graphic from "@arcgis/core/Graphic";

interface FeatureWorkOrderGroupProps {
    feature: Graphic | null | undefined;
    isEditing?: boolean;
    canBeSentToFdv: boolean;
    canHaveFdvWorkOrders: boolean;
    selectable?: boolean;
    selectedWorkOrderIds?: string[];
    setSelectedWorkOrderIds?: Dispatch<SetStateAction<string[]>>;
    workOrderClassFilter?: string;
}

type WorkOrdersUpdatedEventPayload = { gisObjId: number };
type WorkOrdersUpdatedEvent = CustomEvent<WorkOrdersUpdatedEventPayload>;
const WorkOrdersUpdatedEventType = "workorders:updated";

const FeatureWorkOrderGroup = (props: FeatureWorkOrderGroupProps) => {
    const { feature, isEditing, selectable, selectedWorkOrderIds, setSelectedWorkOrderIds, workOrderClassFilter } =
        props;

    const [open, setOpen] = useState(false);
    const [workOrders, setWorkOrders] = useState<Work[]>([]);
    const [showCreateOrderForm, setShowCreateOrderForm] = useState(false);
    const [selectedWorkOrderClass, setSelectedWorkOrderClass] = useState<string | null>(null);
    const [selectedWorkOrderDescription, setSelectedWorkOrderDescription] = useState<string>("");
    const [showWorkOrderLoader, setShowWorkOrderLoader] = useState(false);
    const canCreateWorkOrders =
        props.canBeSentToFdv &&
        (feature?.attributes["nfs_status"] === NFSStatus.PROJECTED ||
            feature?.attributes["nfs_status"] === NFSStatus.PROJECTED_EXISTING);

    const featureRef = useRef<Graphic | null | undefined>(null);

    const reloadWorkOrders = useCallback(async () => {
        if (!feature?.attributes["gis_obj_id"]) {
            return;
        }
        const result = await getWorkOrdersForObject({
            page: 1,
            pageSize: 200,
            gisObjId: feature?.attributes["gis_obj_id"],
            statusCodes: "R,RPL",
            classCodes: workOrderClassFilter || undefined,
        });
        result.items?.sort((a, b) => (a.workOrderID! < b.workOrderID! ? 1 : -1));

        setWorkOrders(result.items || []);
    }, [feature, workOrderClassFilter]);

    useEffect(() => {
        if (!feature?.attributes["gis_obj_id"]) return;

        const handler = (e: Event) => {
            const detail = (e as WorkOrdersUpdatedEvent).detail;
            if (detail?.gisObjId === feature?.attributes["gis_obj_id"]) {
                reloadWorkOrders();
            }
        };

        globalThis.addEventListener(WorkOrdersUpdatedEventType, handler as EventListener);
        return () => globalThis.removeEventListener(WorkOrdersUpdatedEventType, handler as EventListener);
    }, [feature, reloadWorkOrders]);

    useEffect(() => {
        if (featureRef.current !== feature) {
            setWorkOrders([]);
            featureRef.current = feature;
        }

        if (feature) {
            reloadWorkOrders();
        } else {
            setWorkOrders([]);
        }
    }, [feature, reloadWorkOrders]);

    const createFdvOrder = async (): Promise<void> => {
        if (
            !feature?.attributes["gis_obj_id"] ||
            selectedWorkOrderDescription.trim() === "" ||
            !selectedWorkOrderClass
        ) {
            return;
        }

        try {
            setShowWorkOrderLoader(true);

            const orderType =
                feature.attributes["nfs_status"] === NFSStatus.PROJECTED
                    ? WorkOrderTypes.PROJECT_NEW_CONSTRUCTION
                    : WorkOrderTypes.RENEWALMODIFICATION;

            const workOrderId = await createWorkOrder(
                {
                    description: selectedWorkOrderDescription,
                    gisObjId: feature?.attributes["gis_obj_id"],
                    classCode: selectedWorkOrderClass,
                    departmentCode: `${feature.attributes["kystverk_region"]}`, // they want integer as string
                    statusCode: "RPL",
                } as CreateWorkOrderRequest,
                orderType
            );

            console.log("Created work order with ID:", workOrderId);

            // Broadcast so all instances (this one and display-only) can refresh
            globalThis.dispatchEvent(
                new CustomEvent(WorkOrdersUpdatedEventType, {
                    detail: { gisObjId: feature?.attributes["gis_obj_id"] } as WorkOrdersUpdatedEventPayload,
                } as CustomEventInit<WorkOrdersUpdatedEventPayload>)
            );
            toast.info(`Arbeidsordre ${workOrderId} opprettet i FDV. Refresher AO liste...`);
        } catch (error) {
            console.error("Error creating work order:", error);
            toast.error(
                "Feil ved oppretting av arbeidsordre: " + (error instanceof Error ? error.message : "Ukjent feil")
            );
        } finally {
            setShowWorkOrderLoader(false);
            setSelectedWorkOrderDescription("");
            setSelectedWorkOrderClass(null);
            setShowCreateOrderForm(false);
        }
    };

    if (!props.canBeSentToFdv && !props.canHaveFdvWorkOrders) {
        return null;
    }

    return (
        <div className={`feature-work-order-group-container ${selectable ? "selectable" : ""}`}>
            <button className="feature-work-order-group" onClick={() => setOpen(!open)}>
                {selectable ? (
                    <span className="feature-work-order-group-title">
                        Velg AO for oppdatering {`(${selectedWorkOrderIds?.length ?? 0}/${workOrders?.length ?? 0})`}
                    </span>
                ) : (
                    <span className="feature-work-order-group-title">
                        Arbeidsordre {`(${workOrders?.length ?? 0})`}
                    </span>
                )}

                <ArrowDownIcon style={{ rotate: open ? "180deg" : "" }} />
            </button>
            {open && (
                <div className="feature-work-order-group-list-container">
                    {workOrders && workOrders?.length > 0 ? (
                        <div className="feature-work-order-group-list">
                            {workOrders.map((wo, index) => {
                                const isChecked = selectedWorkOrderIds?.includes(wo.workOrderID!) ?? false;

                                return (
                                    <div
                                        key={wo.workOrderID ?? `wo-${index}`}
                                        className="feature-work-order-group-list-item"
                                    >
                                        {selectable && setSelectedWorkOrderIds && wo.workOrderID !== null && (
                                            <Checkbox
                                                id={`select-workorder-${wo.workOrderID}`}
                                                checked={isChecked}
                                                onChange={(checked) => {
                                                    if (checked) {
                                                        setSelectedWorkOrderIds((prev) => {
                                                            const newValue = [...(prev ?? []), wo.workOrderID!];
                                                            console.log("Selected work order IDs:", newValue);
                                                            return newValue;
                                                        });
                                                    } else {
                                                        setSelectedWorkOrderIds((prev) => {
                                                            const newValue =
                                                                prev?.filter((id) => id !== wo.workOrderID) ?? [];
                                                            console.log("Selected work order IDs:", newValue);
                                                            return newValue;
                                                        });
                                                    }
                                                }}
                                            />
                                        )}
                                        <div className="feature-work-order-group-list-item-content">
                                            <a
                                                href={`${AppConfig.FDV.BaseUrl}${AppConfig.FDV.WorkOrderBase}${wo.workOrderID}`}
                                                target="_blank"
                                                rel="noopener"
                                                className="fit-content empty-button"
                                            >
                                                <span className="feature-work-order-item-title">
                                                    {wo.workOrderID} - {wo.workOrderType}
                                                </span>
                                                <OpenInNewWindowIcon height={12} />
                                            </a>
                                            <span>Status: {wo.workOrderStatus}</span>
                                            <span className="flex-row w-100 center">
                                                <span className="flex-row w-100 feature-work-order-item-description">
                                                    {wo.workOrderDescription}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="feature-work-order-group-no-items">Ingen arbeidsordre</div>
                    )}

                    {isEditing && canCreateWorkOrders && (
                        <>
                            {!showCreateOrderForm && (
                                <NfsButton
                                    outlined
                                    disabled={!feature?.attributes["gis_obj_id"]}
                                    onClick={() => setShowCreateOrderForm(true)}
                                >
                                    Opprett ny arbeidsordre i FDV
                                </NfsButton>
                            )}
                            {showCreateOrderForm && (
                                <div>
                                    <div className="feature-work-order-group-create-form">
                                        <strong>Opprett ny arbeidsordre</strong>

                                        <InputField
                                            value={selectedWorkOrderDescription}
                                            type="text"
                                            inputFieldId="new-workorder-description"
                                            label="Beskrivelse"
                                            onChange={(val) => setSelectedWorkOrderDescription(val)}
                                        />

                                        <NfsDropdown
                                            options={["*", "DIR"]}
                                            selectedOption={selectedWorkOrderClass}
                                            setSelectedOption={setSelectedWorkOrderClass}
                                            placeholder="Velg klasse"
                                        />

                                        <NfsButton
                                            onClick={createFdvOrder}
                                            disabled={
                                                !selectedWorkOrderClass || selectedWorkOrderDescription.trim() === ""
                                            }
                                        >
                                            Opprett arbeidsordre
                                        </NfsButton>
                                        <NfsButton outlined onClick={() => setShowCreateOrderForm(false)}>
                                            Avbryt
                                        </NfsButton>
                                    </div>
                                    {showWorkOrderLoader && (
                                        <Loader
                                            color="#000667"
                                            overlay="element"
                                            targetElement=".feature-work-order-group-create-form"
                                            size={40}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default FeatureWorkOrderGroup;
